<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="m-0">SEO by URLs</h4>
      <div class="d-flex align-items-center">
        <el-select
          v-model="lang"
          placeholder="Language"
          style="width: 120px"
          class="mr-3"
          @change="onLangChange"
        >
          <el-option v-for="l in langs" :key="l" :label="l.toUpperCase()" :value="l" />
        </el-select>
        <el-input
          v-model="search"
          placeholder="Search by URL"
          clearable
          style="width: 280px"
          class="mr-3"
          @input="onSearch"
        />
        <el-button type="primary" @click="openCreate">Create</el-button>
      </div>
    </div>

    <card>
      <el-table :data="items" v-loading="loading" style="width: 100%">
        <el-table-column label="URL template" prop="url" min-width="300" />
        <el-table-column label="Title" prop="title" min-width="220" />
        <el-table-column label="Description" prop="description" min-width="220" />
        <el-table-column label="H1" prop="h1" min-width="180" />
        <el-table-column label="Updated" width="160">
          <template slot-scope="{ row }">{{ formatDate(row.updated_at) }}</template>
        </el-table-column>
        <el-table-column fixed="right" label="Edit" width="90" align="center">
          <template slot-scope="{ row }">
            <el-button size="mini" @click="openEdit(row)">Edit</el-button>
          </template>
        </el-table-column>
        <el-table-column fixed="right" label="Delete" width="90" align="center">
          <template slot-scope="{ row }">
            <el-button size="mini" type="danger" @click="handleDelete(row)">Del</el-button>
          </template>
        </el-table-column>
      </el-table>

      <p v-if="!loading && !items.length" class="text-muted text-center mt-3 mb-0">
        Nothing found.
      </p>

      <el-pagination
        v-if="total > perPage"
        class="mt-3"
        layout="prev, pager, next, total"
        :current-page="page"
        :page-size="perPage"
        :total="total"
        @current-change="goToPage"
      />
    </card>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="720px">
      <div v-loading="loadingTexts">
        <div class="form-group">
          <label>URL template</label>
          <el-input v-model="form.url" :disabled="!creating" placeholder="/clinics/country=turkey/" />
        </div>

        <div class="form-group">
          <label>Language</label>
          <el-select v-model="form.lang" style="width: 120px" @change="loadTexts">
            <el-option v-for="l in langs" :key="l" :label="l.toUpperCase()" :value="l" />
          </el-select>
          <small class="text-muted ml-3">
            Texts are per language — switch here to edit another one for the same URL.
          </small>
        </div>

        <div v-for="field in textFields" :key="field.key" class="form-group">
          <label>{{ field.label }}</label>
          <el-input
            v-model="form.fields[field.key]"
            :type="field.multiline ? 'textarea' : 'text'"
            :rows="2"
          />
        </div>
      </div>

      <span slot="footer">
        <el-button @click="dialogVisible = false">Cancel</el-button>
        <el-button type="primary" :loading="saving" @click="save">Save</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import APIManualSeo from "@/api/modules/manual-seo";

// exactly the aliases the seo service accepts, and the labels the old admin used
const TEXT_FIELDS = [
  { key: "title", label: "Title" },
  { key: "description", label: "Description", multiline: true },
  { key: "h1", label: "H1" },
  { key: "h2_offers", label: "H2 Offers" },
  { key: "h2_promo_offers", label: "H2 Promo Offers" },
  { key: "h2_clinics", label: "H2 Clinics" },
  { key: "h2_doctors", label: "H2 Doctors" },
];

const emptyFields = () =>
  TEXT_FIELDS.reduce((fields, { key }) => ({ ...fields, [key]: "" }), {});

export default {
  name: "ManualSeoList",
  data() {
    return {
      items: [],
      total: 0,
      page: 1,
      perPage: 20,
      search: "",
      lang: "en",
      loading: false,
      loadingTexts: false,
      saving: false,
      searchTimer: null,
      dialogVisible: false,
      creating: false,
      form: { url: "", lang: "en", fields: emptyFields() },
      // what the form was loaded with, so only real edits are sent
      original: emptyFields(),
    };
  },
  computed: {
    ...mapState("cmsApiCatalogs", ["domains"]),
    textFields() {
      return TEXT_FIELDS;
    },
    langs() {
      return (this.domains || []).map(({ language }) => language).filter(Boolean);
    },
    dialogTitle() {
      return this.creating ? "New URL template" : this.form.url;
    },
  },
  methods: {
    ...mapActions("cmsApiCatalogs", ["getDomains"]),
    async load() {
      this.loading = true;
      try {
        const { data } = await APIManualSeo.getList(this.lang, {
          q: this.search,
          page: this.page,
          perPage: this.perPage,
        });
        this.items = data.items || [];
        this.total = data.total || 0;
      } catch (e) {
        this.notifyError(e, "Could not load the SEO templates");
      } finally {
        this.loading = false;
      }
    },
    onLangChange() {
      this.page = 1;
      this.$router.replace({ query: { ...this.$route.query, lang: this.lang } }).catch(() => {});
      this.load();
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.load();
      }, 300);
    },
    goToPage(page) {
      this.page = page;
      this.load();
    },
    openCreate() {
      this.creating = true;
      this.form = { url: "", lang: this.lang, fields: emptyFields() };
      this.original = emptyFields();
      this.dialogVisible = true;
    },
    openEdit(row) {
      this.creating = false;
      this.form = { url: row.url, lang: this.lang, fields: emptyFields() };
      this.original = emptyFields();
      this.dialogVisible = true;
      this.loadTexts();
    },
    /**
     * Always re-read: the row in the table holds the listing language, and the
     * dialog can be switched to any other one.
     */
    async loadTexts() {
      if (this.creating || !this.form.url) return;

      this.loadingTexts = true;
      try {
        const { data } = await APIManualSeo.getOne(this.form.lang, this.form.url);
        // `[]` is the service's answer for "nothing written in this language"
        const texts = Array.isArray(data) ? {} : data || {};
        const fields = emptyFields();
        Object.keys(fields).forEach((key) => {
          fields[key] = texts[key] || "";
        });
        this.form.fields = fields;
        this.original = { ...fields };
      } catch (e) {
        this.notifyError(e, "Could not load the texts");
      } finally {
        this.loadingTexts = false;
      }
    },
    /**
     * Only what changed: sending every field would write an empty
     * `domains_texts` row for each one the editor never touched.
     */
    changedFields() {
      return Object.keys(this.form.fields).reduce((payload, key) => {
        const value = (this.form.fields[key] || "").trim();
        if (value === (this.original[key] || "")) return payload;

        return { ...payload, [key]: value };
      }, {});
    },
    async save() {
      const url = (this.form.url || "").trim();
      if (!url) {
        this.$notify({ type: "warning", title: "URL template is required" });

        return;
      }

      const fields = this.changedFields();
      if (!Object.keys(fields).length) {
        this.dialogVisible = false;

        return;
      }

      this.saving = true;
      try {
        await APIManualSeo.save(this.form.lang, url, fields);
        this.$notify({ type: "success", title: "Saved" });
        this.dialogVisible = false;
        await this.load();
      } catch (e) {
        this.notifyError(e, "Could not save");
      } finally {
        this.saving = false;
      }
    },
    async handleDelete(row) {
      try {
        await this.$confirm(
          `Delete the ${this.lang.toUpperCase()} texts of "${row.url}"? `
            + "The URL template itself goes when no language is left on it.",
          "Warning",
          { confirmButtonText: "Delete", cancelButtonText: "Cancel", type: "warning" }
        );
        this.loading = true;
        await APIManualSeo.remove(this.lang, row.url);
        this.$notify({ type: "success", title: "Deleted" });
        await this.load();
      } catch (e) {
        if (e !== "cancel") {
          this.notifyError(e, "Could not delete");
        }
      } finally {
        this.loading = false;
      }
    },
    formatDate(value) {
      if (!value) return "—";

      return new Date(value * 1000).toISOString().slice(0, 16).replace("T", " ");
    },
    notifyError(error, fallback) {
      const payload = error && error.response && error.response.data;
      const code = payload && payload.error && payload.error.code;
      this.$notify({ type: "warning", title: code || (error && error.message) || fallback });
    },
  },
  async created() {
    if (this.$route.query.lang) {
      this.lang = this.$route.query.lang;
    }
    await this.getDomains();
    if (this.langs.length && !this.langs.includes(this.lang)) {
      this.lang = this.langs[0];
    }
    this.load();
  },
};
</script>

<style scoped>
.el-select {
  margin-bottom: 0;
}
</style>
